import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { increasePrice, matchesSearchTerms, searchTerms, variantLabel, type RoundingMode } from '@pos/core';
import { applyLocalChanges } from '@pos/local-db';
import { useSession } from '../../app/current.tsx';
import { db } from '../../lib/db.ts';
import { requestSync } from '../../services/sync.ts';
import { Alert, Button, Card, Field, Input, PageHeader, Select, Spinner, errorMessage } from '../../ui/index.tsx';
import { Dialog } from '../../ui/Dialog.tsx';
import { buildPriceIncreaseChanges } from './changes.ts';
import { useCatalog, useMoney } from './data.ts';

const ROUNDING = [
  { cents: 1, label: 'Sin redondear' },
  { cents: 1000, label: 'Hacia arriba, a $10' },
  { cents: 5000, label: 'Hacia arriba, a $50' },
  { cents: 10000, label: 'Hacia arriba, a $100' },
];
const CHUNK = 1000;
const precios = (n: number) => `${n.toLocaleString('es-AR')} precio${n === 1 ? '' : 's'}`;

export function BulkPricePage() {
  const { writer } = useSession();
  const catalog = useCatalog();
  const money = useMoney();
  const [category, setCategory] = useState('');
  const [supplier, setSupplier] = useState('');
  const [query, setQuery] = useState('');
  const [percentText, setPercentText] = useState('');
  const [rounding, setRounding] = useState(1000);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  const percent = Number(percentText.replace(',', '.'));
  const validPercent = percentText.trim() !== '' && Number.isFinite(percent) && percent > -100 && percent <= 1000 && percent !== 0;
  const mode: RoundingMode = 'up';

  const selection = useMemo(() => {
    if (!catalog) return [];
    const terms = searchTerms(query);
    return catalog.items
      .filter(
        (i) =>
          i.product.active &&
          (!category || i.category?.id === category) &&
          (!supplier || i.supplier?.id === supplier) &&
          matchesSearchTerms(i.search, terms),
      )
      .flatMap((i) => i.variants.filter((v) => v.active).map((v) => ({ item: i, variant: v })));
  }, [catalog, category, supplier, query]);

  const changes = useMemo(
    () => (validPercent ? buildPriceIncreaseChanges(selection.map((s) => s.variant), percent, rounding, mode) : []),
    [selection, percent, rounding, validPercent, mode],
  );

  if (!catalog) {
    return (
      <div className="flex justify-center py-12 text-slate-500">
        <Spinner />
      </div>
    );
  }

  const apply = async () => {
    setBusy(true);
    try {
      for (let i = 0; i < changes.length; i += CHUNK) {
        await applyLocalChanges(db, changes.slice(i, i + CHUNK), writer);
      }
      requestSync(0);
      setResult({ tone: 'success', text: `Listo: se actualizaron ${precios(changes.length)}.` });
      setPercentText('');
    } catch (e) {
      setResult({ tone: 'error', text: errorMessage(e) });
    } finally {
      setBusy(false);
      setConfirm(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <PageHeader
        title="Aumento masivo de precios"
        description="Subí (o bajá) los precios de muchos productos a la vez, con redondeo."
        actions={
          <Link to="/productos">
            <Button variant="secondary">Volver</Button>
          </Link>
        }
      />

      <Card className="space-y-4">
        <h2 className="font-semibold">1. ¿Qué productos?</h2>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Categoría">
            {(id) => (
              <Select id={id} value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="">Todas</option>
                {catalog.categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Proveedor">
            {(id) => (
              <Select id={id} value={supplier} onChange={(e) => setSupplier(e.target.value)}>
                <option value="">Todos</option>
                {catalog.suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Que contengan">{(id) => <Input id={id} placeholder="Ej. coca" value={query} onChange={(e) => setQuery(e.target.value)} />}</Field>
        </div>
        <p className="text-sm text-slate-500">{precios(selection.length)} seleccionado{selection.length === 1 ? '' : 's'} (solo productos activos).</p>
      </Card>

      <Card className="space-y-4">
        <h2 className="font-semibold">2. ¿Cuánto?</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Porcentaje" hint="Ej. 10 para subir un 10 %, -5 para bajar un 5 %.">
            {(id) => (
              <div className="relative">
                <Input id={id} inputMode="decimal" value={percentText} onChange={(e) => setPercentText(e.target.value)} className="pr-8" />
                <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-slate-500">%</span>
              </div>
            )}
          </Field>
          <Field label="Redondeo">
            {(id) => (
              <Select id={id} value={rounding} onChange={(e) => setRounding(Number(e.target.value))}>
                {ROUNDING.map((r) => (
                  <option key={r.cents} value={r.cents}>
                    {r.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
      </Card>

      {validPercent && selection.length > 0 && (
        <Card className="space-y-3">
          <h2 className="font-semibold">3. Revisá y aplicá</h2>
          <p className="text-sm text-slate-600">
            {changes.length === 1 ? 'Cambia' : 'Cambian'} <strong>{precios(changes.length)}</strong>. Primeros ejemplos:
          </p>
          <div className="divide-y divide-slate-100 rounded-lg ring-1 ring-slate-200">
            {selection.slice(0, 8).map(({ item, variant }) => (
              <div key={variant.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                <span className="min-w-0 truncate">
                  {item.product.name}
                  {variant.attributes && Object.keys(variant.attributes).length > 0 && (
                    <span className="text-slate-500"> · {variantLabel(variant.attributes)}</span>
                  )}
                </span>
                <span className="shrink-0 whitespace-nowrap">
                  <span className="text-slate-500">{money(variant.price)}</span> →{' '}
                  <strong>{money(increasePrice(variant.price, percent, rounding, mode))}</strong>
                </span>
              </div>
            ))}
          </div>
          <div className="flex justify-end">
            <Button size="lg" disabled={changes.length === 0} onClick={() => setConfirm(true)}>
              Aplicar a {precios(changes.length)}
            </Button>
          </div>
        </Card>
      )}

      {result && <Alert tone={result.tone}>{result.text}</Alert>}

      <Dialog
        open={confirm}
        onClose={() => setConfirm(false)}
        title="¿Aplicar el aumento?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirm(false)}>
              Cancelar
            </Button>
            <Button onClick={apply} loading={busy}>
              Sí, aplicar
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-600">
          Se van a cambiar {precios(changes.length)} ({percent > 0 ? '+' : ''}
          {percentText.replace('.', ',')} %). Queda registrado en el historial de precios de cada producto.
        </p>
      </Dialog>
    </div>
  );
}
